import { useCallback, useState } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, FileText, Link2, Loader2, Trash2, UploadCloud, Video } from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";

const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_TOTAL_BYTES = 100 * 1024 * 1024;
const ACCEPTED_DOCUMENTS = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
  "application/vnd.ms-powerpoint": [".ppt"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

type Resource = {
  id: number;
  kind: "document" | "video";
  label: string;
  originalFileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  videoUrl: string | null;
  providerTitle: string | null;
  providerThumbnailUrl: string | null;
  sortOrder: number;
  downloadUrl: string | null;
};
type UploadItem = { id: string; file: File; status: "uploading" | "done" | "failed"; error?: string };

function fileSize(bytes: number | null) {
  if (!bytes) return "";
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
}
function readAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("The file could not be read."));
    reader.readAsDataURL(file);
  });
}

/** Admin-only attachment and recording editor. Resource authorization stays server-side. */
export default function EventResourceManager({ eventSlug }: { eventSlug: string }) {
  const utils = trpc.useUtils();
  const resourcesQuery = trpc.events.adminResources.useQuery({ eventSlug }, { retry: false });
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [dropError, setDropError] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoLabel, setVideoLabel] = useState("");
  const [videoError, setVideoError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Resource | null>(null);
  const [draggedResourceId, setDraggedResourceId] = useState<number | null>(null);
  const invalidate = useCallback(async () => {
    await Promise.all([
      utils.events.adminResources.invalidate({ eventSlug }),
      utils.events.resourceAvailability.invalidate({ eventSlug }),
      utils.events.memberResources.invalidate({ eventSlug }),
    ]);
  }, [eventSlug, utils]);

  const uploadDocument = trpc.events.uploadResourceDocument.useMutation({
    onSuccess: async () => { await invalidate(); },
  });
  const addVideo = trpc.events.addResourceVideo.useMutation({
    onSuccess: async () => { await invalidate(); setVideoUrl(""); setVideoLabel(""); setVideoError(null); chiNotify.success("Recording added"); },
    onError: error => { setVideoError(error.message); chiNotify.error("Recording not added", { description: error.message }); },
  });
  const updateResource = trpc.events.updateResource.useMutation({
    onSuccess: async () => { await invalidate(); },
    onError: error => chiNotify.error("Resource not updated", { description: error.message }),
  });
  const reorderResources = trpc.events.reorderResources.useMutation({
    onSuccess: async () => { await invalidate(); },
    onError: error => chiNotify.error("Resources not reordered", { description: error.message }),
  });
  const deleteResource = trpc.events.deleteResource.useMutation({
    onSuccess: async () => { await invalidate(); setDeleteTarget(null); chiNotify.success("Resource deleted"); },
    onError: async error => {
      // Access is revoked in the database before best-effort object cleanup, so
      // refresh even when cleanup reports an orphan rather than leaving a row
      // visible that the server has already removed.
      await invalidate();
      setDeleteTarget(null);
      chiNotify.error("Resource removed, but cleanup needs attention", { description: error.message });
    },
  });

  const onDrop = useCallback(async (accepted: File[], rejected: FileRejection[]) => {
    const currentBytes = uploads.filter(item => item.status !== "failed").reduce((total, item) => total + item.file.size, 0);
    const allowed = accepted.filter(file => file.size <= MAX_FILE_BYTES);
    if (rejected.length || allowed.length !== accepted.length) {
      setDropError("Choose PDF, PPTX, PPT, DOCX, or XLSX files no larger than 25 MB each.");
    } else if (currentBytes + allowed.reduce((total, file) => total + file.size, 0) > MAX_TOTAL_BYTES) {
      setDropError("Attachments cannot exceed 100 MB total. Remove a file and try again.");
      return;
    } else setDropError(null);
    const additions = allowed.map((file, index) => ({ id: `${file.name}-${file.size}-${Date.now()}-${index}`, file, status: "uploading" as const }));
    setUploads(current => [...current, ...additions]);
    await Promise.all(additions.map(async item => {
      try {
        await uploadDocument.mutateAsync({ eventSlug, fileName: item.file.name, contentType: item.file.type, dataUrl: await readAsDataUrl(item.file) });
        setUploads(current => current.map(row => row.id === item.id ? { ...row, status: "done" } : row));
      } catch (error) {
        setUploads(current => current.map(row => row.id === item.id ? { ...row, status: "failed", error: error instanceof Error ? error.message : "Upload failed." } : row));
      }
    }));
  }, [eventSlug, uploadDocument, uploads]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: ACCEPTED_DOCUMENTS, maxSize: MAX_FILE_BYTES, multiple: true });
  const resources = (resourcesQuery.data ?? []) as unknown as Resource[];

  function move(resource: Resource, direction: -1 | 1) {
    const index = resources.findIndex(item => item.id === resource.id);
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= resources.length) return;
    const ids = resources.map(item => item.id);
    [ids[index], ids[swapIndex]] = [ids[swapIndex], ids[index]];
    reorderResources.mutate({ eventSlug, resourceIds: ids });
  }

  function moveBefore(targetId: number) {
    if (draggedResourceId === null || draggedResourceId === targetId) return;
    const ids = resources.map(item => item.id);
    const fromIndex = ids.indexOf(draggedResourceId);
    const targetIndex = ids.indexOf(targetId);
    if (fromIndex < 0 || targetIndex < 0) return;
    ids.splice(fromIndex, 1);
    ids.splice(targetIndex, 0, draggedResourceId);
    setDraggedResourceId(null);
    reorderResources.mutate({ eventSlug, resourceIds: ids });
  }

  return <section aria-labelledby="event-resources-heading" style={{ gridColumn: "1 / -1", display: "grid", gap: ".9rem", padding: "1rem", borderRadius: ".75rem", border: "1px solid rgba(0,212,255,.16)", background: "rgba(0,212,255,.035)" }}>
    <div>
      <p id="event-resources-heading" style={{ margin: 0, color: "#CDD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".06em", textTransform: "uppercase" }}>Attachments & recordings</p>
      <p style={{ margin: ".3rem 0 0", color: "#718096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Share post-event documents and recording links with paid members. Documents: PDF, PPTX, PPT, DOCX, or XLSX — 25 MB each, 100 MB total.</p>
    </div>
    <div {...getRootProps()} style={{ display: "grid", placeItems: "center", gap: ".35rem", padding: "1.2rem", borderRadius: ".65rem", border: `1.5px dashed ${isDragActive ? "rgba(0,212,255,.8)" : "rgba(255,255,255,.16)"}`, cursor: "pointer" }}>
      <input {...getInputProps()} />
      <UploadCloud size={22} aria-hidden="true" color="#7DEAFF" />
      <strong style={{ color: "#E2E8F0", fontSize: "var(--chi-body-compact)" }}>{isDragActive ? "Drop documents to upload" : "Drag documents here, or click to choose"}</strong>
    </div>
    {dropError && <p role="alert" style={{ margin: 0, color: "#FDA4AF", fontSize: "var(--chi-body-compact)" }}><AlertCircle size={14} aria-hidden="true" /> {dropError}</p>}
    {uploads.length > 0 && <ul aria-label="Document upload status" style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: ".35rem" }}>{uploads.map(item => <li key={item.id} style={{ display: "flex", gap: ".5rem", alignItems: "center", color: "#CBD5E1", fontSize: "var(--chi-body-compact)" }}><FileText size={15} aria-hidden="true" /> <span style={{ flex: 1 }}>{item.file.name}</span>{item.status === "uploading" && <Loader2 className="animate-spin" size={15} aria-label="Uploading" />}{item.status === "done" && <CheckCircle2 size={15} color="#86EFAC" aria-label="Uploaded" />}{item.status === "failed" && <span role="alert" style={{ color: "#FDA4AF" }}>{item.error}</span>}</li>)}</ul>}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: ".5rem" }}>
      <input aria-label="Recording URL" type="url" value={videoUrl} onChange={event => { setVideoUrl(event.target.value); setVideoError(null); }} placeholder="YouTube or Vimeo URL" style={{ minWidth: 0, padding: ".6rem", borderRadius: ".45rem", border: "1px solid rgba(255,255,255,.16)", background: "#111827", color: "white" }} />
      <input aria-label="Recording label (optional)" value={videoLabel} onChange={event => setVideoLabel(event.target.value)} placeholder="Label (optional)" style={{ minWidth: 0, padding: ".6rem", borderRadius: ".45rem", border: "1px solid rgba(255,255,255,.16)", background: "#111827", color: "white" }} />
      <button type="button" disabled={!videoUrl.trim() || addVideo.isPending} onClick={() => addVideo.mutate({ eventSlug, url: videoUrl.trim(), label: videoLabel.trim() || undefined })} style={{ minHeight: 38, padding: "0 .7rem", borderRadius: ".45rem", border: 0, background: "#007C96", color: "white", cursor: "pointer" }}><Link2 size={14} aria-hidden="true" /> Add video</button>
    </div>
    {videoError && <p role="alert" style={{ margin: 0, color: "#FDA4AF", fontSize: "var(--chi-body-compact)" }}>{videoError}</p>}
    {resourcesQuery.isError && <p role="alert" style={{ margin: 0, color: "#FDA4AF" }}>{resourcesQuery.error.message}</p>}
    <ol aria-label="Published resources" style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: ".45rem" }}>{resources.map((resource, index) => <li
      key={resource.id}
      draggable={!reorderResources.isPending}
      onDragStart={() => setDraggedResourceId(resource.id)}
      onDragEnd={() => setDraggedResourceId(null)}
      onDragOver={event => event.preventDefault()}
      onDrop={event => { event.preventDefault(); moveBefore(resource.id); }}
      style={{ display: "flex", alignItems: "center", gap: ".5rem", padding: ".55rem", borderRadius: ".5rem", background: draggedResourceId === resource.id ? "rgba(0,212,255,.09)" : "rgba(255,255,255,.035)", cursor: "grab" }}
    >
      {resource.kind === "video" && resource.providerThumbnailUrl
        ? <img src={resource.providerThumbnailUrl} alt="" style={{ width: 52, aspectRatio: "16 / 9", objectFit: "cover", borderRadius: ".3rem" }} />
        : resource.kind === "video"
          ? <Video size={16} aria-hidden="true" />
          : <FileText size={16} aria-hidden="true" />}
      <input aria-label={`Label for ${resource.label}`} defaultValue={resource.label} onBlur={event => { const label = event.currentTarget.value.trim(); if (label && label !== resource.label) updateResource.mutate({ resourceId: resource.id, label }); }} style={{ flex: 1, minWidth: 0, padding: ".35rem", borderRadius: ".35rem", border: "1px solid rgba(255,255,255,.12)", background: "#111827", color: "white" }} />
      <button type="button" aria-label={`Move ${resource.label} up`} disabled={index === 0} onClick={() => move(resource, -1)}><ChevronUp size={16} /></button><button type="button" aria-label={`Move ${resource.label} down`} disabled={index === resources.length - 1} onClick={() => move(resource, 1)}><ChevronDown size={16} /></button><button type="button" aria-label={`Delete ${resource.label}`} onClick={() => setDeleteTarget(resource)}><Trash2 size={16} /></button>
    </li>)}</ol>
    <AlertDialog open={Boolean(deleteTarget)} onOpenChange={open => { if (!open) setDeleteTarget(null); }}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete this resource?</AlertDialogTitle><AlertDialogDescription>{deleteTarget?.label} will no longer be available to members.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteTarget && deleteResource.mutate({ resourceId: deleteTarget.id })}>Delete resource</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
  </section>;
}